// S15 — Dashboard analytics BE client.
//
// BE endpoints (under `/api/v1/admin/dashboard`):
//   GET /api/v1/admin/dashboard/summary                      — top-level KPI tiles + 7-day trend
//   GET /api/v1/admin/dashboard/metrics/{key}/series         — historic series for a single metric
//
// The series endpoint requires `from` and `to` query params (DateOnly: yyyy-MM-dd).
// The BE controller declares them without `?`, so we always send a window.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type { components } from '@/types/api';

export type DashboardSummaryDto = components['schemas']['DashboardSummaryDto'];
export type DashboardMetricTileDto = components['schemas']['DashboardMetricTileDto'];
export type DashboardSeriesPointDto = components['schemas']['DashboardSeriesPointDto'];

export interface DashboardSummary {
  date: string;
  tiles: Array<{
    metricKey: string;
    value: number;
    previousValue: number;
    deltaPercent: number;
  }>;
  sevenDayTrend: Array<{
    date: string;
    metricKey: string;
    value: number;
  }>;
}

export interface DashboardSeriesPoint {
  date: string;
  metricKey: string;
  value: number;
}

function mapSummary(d: DashboardSummaryDto): DashboardSummary {
  return {
    date: d.date ?? '',
    tiles: (d.tiles ?? []).map((t) => ({
      metricKey: t.metricKey ?? '',
      value: t.value ?? 0,
      previousValue: t.previousValue ?? 0,
      deltaPercent: t.deltaPercent ?? 0,
    })),
    sevenDayTrend: (d.sevenDayTrend ?? []).map((p) => ({
      date: p.date ?? '',
      metricKey: p.metricKey ?? '',
      value: p.value ?? 0,
    })),
  };
}

function mapPoint(p: DashboardSeriesPointDto): DashboardSeriesPoint {
  return {
    date: p.date ?? '',
    metricKey: p.metricKey ?? '',
    value: p.value ?? 0,
  };
}

const BASE = '/api/v1/admin/dashboard';

export const dashboardApi = {
  /** Top-level KPI tiles + 7-day trend (BE rolls the date window itself). */
  async summary(): Promise<DashboardSummary> {
    const res = await http.get<ActionResponse<DashboardSummaryDto>>(`${BASE}/summary`);
    return mapSummary(unwrap(res));
  },

  /**
   * Historic series for a metric.
   *
   * ISSUE-63 — BE marks `from` and `to` as required (`[BindRequired] [Required]`,
   * `required: true` in OpenAPI). We mirror that contract on the FE: both
   * arguments are non-nullable, and we throw eagerly if either resolves to
   * an empty string so a UI-side mistake fails loudly instead of producing a
   * silent 400 round-trip.
   */
  async series(
    key: string,
    from: string | Date,
    to: string | Date,
  ): Promise<DashboardSeriesPoint[]> {
    const fromStr = typeof from === 'string' ? from : from.toISOString().slice(0, 10);
    const toStr = typeof to === 'string' ? to : to.toISOString().slice(0, 10);
    if (!fromStr || !toStr) {
      throw new Error(
        'dashboardApi.series: `from` and `to` are required (ISSUE-63).',
      );
    }
    const qs = new URLSearchParams({ from: fromStr, to: toStr }).toString();
    const res = await http.get<ActionResponse<DashboardSeriesPointDto>>(
      `${BASE}/metrics/${encodeURIComponent(key)}/series?${qs}`,
    );
    const { items } = unwrapList(res);
    return items.map(mapPoint);
  },
};
